'use client';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, Send } from 'lucide-react';

export interface QueryFormData {
  domain: string;
  description: string;
  complexity: 'ADVISORY' | 'DIAGNOSTIC' | 'STRATEGIC';
  budgetMin: number;
  budgetMax: number;
  urgency: 'FLEXIBLE' | 'WITHIN_WEEK' | 'URGENT';
}

interface QueryFormProps {
  onSubmit: (data: QueryFormData) => void;
  loading: boolean;
}

const DOMAINS = [
  'FMCG', 'M&A', 'Due Diligence', 'Manufacturing', 'Supply Chain',
  'Healthcare', 'Digital Transformation', 'Private Equity', 'Real Estate',
  'D2C', 'Logistics', 'Financial Services', 'Brand Strategy', 'Capital Markets',
  'EdTech', 'Regulatory Affairs', 'HR & Talent', 'ESG & Sustainability',
];

export default function QueryForm({ onSubmit, loading }: QueryFormProps) {
  const { register, handleSubmit, watch, formState: { errors } } = useForm<QueryFormData>({
    defaultValues: {
      complexity: 'ADVISORY',
      urgency: 'FLEXIBLE',
      budgetMin: 0,
      budgetMax: 0,
    },
  });

  const budgetMin = watch('budgetMin');
  const budgetMax = watch('budgetMax');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="surface-elevated p-6 flex flex-col gap-5 sticky top-24">
      <h2 className="font-heading text-lg text-foreground">Query Details</h2>

      <div>
        <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5" htmlFor="domain">
          Domain
        </label>
        <p className="text-xs text-muted-foreground mb-2">The primary expertise area you need.</p>
        <select
          id="domain"
          {...register('domain', { required: 'Please select a domain' })}
          className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary/50 transition-colors cursor-pointer"
          aria-describedby="domain-error"
        >
          <option value="">Select domain…</option>
          {DOMAINS.map((d) => (
            <option key={`domain-opt-${d}`} value={d}>{d}</option>
          ))}
        </select>
        {errors.domain && <p id="domain-error" className="text-xs text-red-400 mt-1">{errors.domain.message}</p>}
      </div>

      <div>
        <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5" htmlFor="description">
          Description
        </label>
        <p className="text-xs text-muted-foreground mb-2">Describe your specific need. More detail = better match.</p>
        <textarea
          id="description"
          {...register('description', { required: 'Please describe your query', minLength: { value: 30, message: 'Please provide at least 30 characters' } })}
          rows={5}
          className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary/50 transition-colors resize-none"
          placeholder="e.g. We are a Series B FMCG brand looking to expand into modern trade. We need strategic guidance on category management, retailer negotiations, and shelf positioning in hypermarkets and supermarkets across Tier 1 cities…"
          aria-describedby="description-error"
        />
        {errors.description && <p id="description-error" className="text-xs text-red-400 mt-1">{errors.description.message}</p>}
      </div>

      <div>
        <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-2">
          Complexity
        </label>
        <div className="grid grid-cols-3 gap-2">
          {([
            { value: 'ADVISORY', label: 'Advisory', desc: 'Quick call or input' },
            { value: 'DIAGNOSTIC', label: 'Diagnostic', desc: 'Problem analysis' },
            { value: 'STRATEGIC', label: 'Strategic', desc: 'Deep engagement' },
          ] as const).map((opt) => (
            <label key={`complexity-${opt.value}`} className="cursor-pointer">
              <input type="radio" value={opt.value} {...register('complexity')} className="sr-only" />
              <div className={`p-3 rounded-lg border text-center transition-all duration-150 ${watch('complexity') === opt.value ? 'border-primary bg-primary/8' : 'border-border hover:border-primary/30'}`}>
                <div className={`text-xs font-mono-data font-medium ${watch('complexity') === opt.value ? 'text-primary' : 'text-foreground'}`}>{opt.label}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{opt.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5">
          Budget Range (₹ total)
        </label>
        <p className="text-xs text-muted-foreground mb-2">Optional. Helps filter appropriate-tier experts.</p>
        <div className="flex items-center gap-2">
          <input
            type="number"
            step="5000"
            min="0"
            {...register('budgetMin', { valueAsNumber: true })}
            placeholder="Min"
            className="flex-1 bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary/50 transition-colors font-mono-data"
            aria-label="Minimum budget"
          />
          <span className="text-muted-foreground text-sm">–</span>
          <input
            type="number"
            step="5000"
            min="0"
            {...register('budgetMax', { valueAsNumber: true })}
            placeholder="Max"
            className="flex-1 bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary/50 transition-colors font-mono-data"
            aria-label="Maximum budget"
          />
        </div>
        {budgetMin > 0 && budgetMax > 0 && (
          <p className="text-xs text-muted-foreground mt-1 font-mono-data">
            ₹{Number(budgetMin).toLocaleString('en-IN')} – ₹{Number(budgetMax).toLocaleString('en-IN')}
          </p>
        )}
      </div>

      <div>
        <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-2">
          Urgency
        </label>
        <div className="grid grid-cols-3 gap-2">
          {([
            { value: 'FLEXIBLE', label: 'Flexible', desc: 'No rush' },
            { value: 'WITHIN_WEEK', label: 'This Week', desc: 'Within 7 days' },
            { value: 'URGENT', label: 'Urgent', desc: 'Rush rate applies' },
          ] as const).map((opt) => (
            <label key={`urgency-${opt.value}`} className="cursor-pointer">
              <input type="radio" value={opt.value} {...register('urgency')} className="sr-only" />
              <div className={`p-3 rounded-lg border text-center transition-all duration-150 ${
                watch('urgency') === opt.value
                  ? opt.value === 'URGENT'? 'border-red-400/50 bg-red-400/8' : 'border-primary bg-primary/8' :'border-border hover:border-primary/30'
              }`}>
                <div className={`text-xs font-mono-data font-medium ${
                  watch('urgency') === opt.value
                    ? opt.value === 'URGENT'? 'text-red-400' : 'text-primary' :'text-foreground'
                }`}>{opt.label}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{opt.desc}</div>
              </div>
            </label>
          ))}
        </div>
        {watch('urgency') === 'URGENT' && (
          <p className="text-xs text-red-400 mt-2 font-mono-data">⚡ Rush rate multiplier will apply to matched expert rates</p>
        )}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 px-6 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {loading ? (
          <><Loader2 size={16} className="animate-spin" />Matching Experts…</>
        ) : (
          <><Send size={14} />Find Matching Experts</>
        )}
      </button>
    </form>
  );
}